import { getStatusKey } from '../../config/constants';
import { AzkListFilterKey, StationFilters } from '@/types/azk-list.types';

export const getOptionsWithAll = (options?: string[]): string[] => {
  const safeOptions = options ?? [];
  return safeOptions.length > 0 ? ['Всі', ...safeOptions] : [];
};

const getFilterLabel = (key: string): string => {
  const labels: { [key: string]: string } = {
    regional_office: 'Регіональне відділення',
    province: 'Область',
    brand: 'Бренд',
    status: 'Статус АЗК'
  };
  return labels[key] || key;
};

export const getActiveFilters = (filters: Omit<StationFilters, 'page' | 'limit' | 'search'>) => {
  return Object.entries(filters).flatMap(([key, val]) => {
    const values: string[] = Array.isArray(val)
      ? val.filter((v): v is string => v !== undefined && v !== null && v !== '')
      : val
        ? [val]
        : [];

    return values.map(v => ({
      key: key as AzkListFilterKey,
      label: getFilterLabel(key),
      value: v
    }));
  });
};

export const getUpdatedFilters = (
  filters: StationFilters,
  filterKey: AzkListFilterKey,
  value: string,
  provincesOptions: string[],
  brandsOptions: string[],
  statusOptions: string[]
): StationFilters => {
  const newFilters: StationFilters = { ...filters };

  if (!value || value === '') {
    delete newFilters[filterKey];

    if (filterKey === 'regional_office') {
      delete newFilters.province;
      delete newFilters.brand;
      delete newFilters.status;
    }

    return newFilters;
  }

  if (value === 'Всі') {
    const optionsMap: Record<string, string[]> = {
      province: provincesOptions,
      brand: brandsOptions,
      status: statusOptions
    };

    if (filterKey in optionsMap) {
      newFilters[filterKey] = optionsMap[filterKey].filter(v => v !== 'Всі');
    }

    return newFilters;
  }

  if (filterKey === 'regional_office') {
    delete newFilters.province;
    delete newFilters.brand;
    delete newFilters.status;

    newFilters[filterKey] = value;
    return newFilters;
  }

  let finalValue = value;
  if (filterKey === 'status') {
    finalValue = getStatusKey(value);
  }

  const current = newFilters[filterKey];
  if (Array.isArray(current)) {
    if (!current.includes(finalValue)) {
      newFilters[filterKey] = [...current, finalValue];
    }
  } else if (typeof current === 'string') {
    if (current !== finalValue) {
      newFilters[filterKey] = [current, finalValue];
    }
  } else {
    newFilters[filterKey] = filterKey === 'status' ? [finalValue] : finalValue;
  }

  return newFilters;
};

export const getFiltersAfterRemove = (
  filters: StationFilters,
  key: AzkListFilterKey,
  value?: string
): StationFilters => {
  const newFilters: StationFilters = { ...filters };

  if (value) {
    const current = newFilters[key];
    if (Array.isArray(current)) {
      const filtered = current.filter(v => v !== value);
      if (filtered.length === 0) {
        delete newFilters[key];
      } else if (filtered.length === 1) {
        newFilters[key] = filtered[0];
      } else {
        newFilters[key] = filtered;
      }
    } else {
      delete newFilters[key];
    }
  } else {
    delete newFilters[key];
  }

  if (key === 'regional_office') {
    delete newFilters.province;
    delete newFilters.brand;
    delete newFilters.status;
  }

  return newFilters;
};

export const extractSettlement = (station: any, address: string): string => {
  if (station?.settlement_name) {
    return String(station.settlement_name).trim();
  }
  if (!address) return '';

  address = address.trim();

  if (address.startsWith('селище')) {
    const words = address.split(/\s+/);
    return words.length > 1 ? words.slice(1).join(' ') : words[0] || '';
  }

  const dotIndex = address.indexOf('.');
  if (dotIndex !== -1) {
    return address.substring(dotIndex + 1).trim();
  }

  const words = address.split(/\s+/);
  return words.length > 1 ? words.slice(1).join(' ') : words[0] || '';
};

export const getFirstNumber = (value: string): number => {
  if (!value || value.trim() === '') return Infinity;
  const match = value.trim().match(/\d+/);
  return match ? parseInt(match[0], 10) : Infinity;
};

export const sortStations = (stations: any[], sortField: string, sortDirection: 'ASC' | 'DESC') => {
  if (!sortField) return stations;
  const direction = sortDirection.toLowerCase() as 'asc' | 'desc';

  return [...stations].sort((a, b) => {
    let aValue: any = (a as any)[sortField];
    let bValue: any = (b as any)[sortField];

    if (sortField === 'unicode') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    } else if (sortField === 'station_number') {
      const aNum = getFirstNumber(String(aValue));
      const bNum = getFirstNumber(String(bValue));
      if (aNum < bNum) return direction === 'asc' ? -1 : 1;
      if (aNum > bNum) return direction === 'asc' ? 1 : -1;
      return 0;
    } else if (sortField === 'address') {
      aValue = String(aValue || '');
      bValue = String(bValue || '');
      const aSettlement = extractSettlement(a, aValue);
      const bSettlement = extractSettlement(b, bValue);
      const comparison = aSettlement.localeCompare(bSettlement, 'uk-UA');
      return direction === 'asc' ? comparison : -comparison;
    } else {
      aValue = String(aValue || '');
      bValue = String(bValue || '');
      const comparison = aValue.localeCompare(bValue, 'uk-UA');
      return direction === 'asc' ? comparison : -comparison;
    }
  });
};

export const getExportFilters = (
  filters: StationFilters,
  searchQuery?: string,
  debouncedSearch?: string
): StationFilters => {
  const exportFilters: StationFilters = { ...filters };
  delete (exportFilters as any).page;
  delete (exportFilters as any).limit;

  const searchValue = (debouncedSearch && debouncedSearch.trim()) || (searchQuery && searchQuery.trim());
  if (searchValue) {
    exportFilters.search = searchValue;
  } else {
    delete (exportFilters as any).search;
  }

  return exportFilters;
};
